/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  ArrowLeft,
  ArrowRight,
  AlertCircle,
  CheckCircle,
  GripVertical,
  MapPin,
  X,
} from 'lucide-react';

interface FieldMappingProps {
  data: {
    headers: string[];
    rows: any[][];
    filename: string;
  };
  onMappingComplete: (mappings: FieldMapping[], processedData: any[]) => void;
  onBack: () => void;
}

interface FieldMapping {
  internalField: string;
  mappedColumn: string | null;
  required: boolean;
}

const INTERNAL_FIELDS = [
  { name: 'First Name', required: true },
  { name: 'Last Name', required: true },
  { name: 'Email Address 1', required: true },
  { name: 'Co-Owner First Name', required: false },
  { name: 'Co-Owner Last Name', required: false },
  { name: 'Email Address 2', required: false },
  { name: 'Telephone', required: false },
  { name: 'Street Address', required: false },
  { name: 'Address Line 2', required: false },
  { name: 'City', required: false },
  { name: 'State', required: false },
  { name: 'Zip Code', required: false },
];

export function FieldMapping({
  data,
  onMappingComplete,
  onBack,
}: FieldMappingProps) {
  const [mappings, setMappings] = useState<FieldMapping[]>([]);
  const [unmappedColumns, setUnmappedColumns] = useState<string[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Auto-suggest mappings based on column headers
  const suggestMappings = (headers: string[]) => {
    const suggestions: FieldMapping[] = INTERNAL_FIELDS.map((field) => ({
      internalField: field.name,
      mappedColumn: null,
      required: field.required,
    }));

    const available = [...headers];

    // Exact and fuzzy matching logic
    const matchPatterns = {
      'First Name': ['first name', 'firstname', 'fname', 'first', 'given name'],
      'Last Name': [
        'last name',
        'lastname',
        'lname',
        'last',
        'surname',
        'family name',
      ],
      'Email Address 1': [
        'email',
        'email address',
        'email 1',
        'primary email',
        'e-mail',
      ],
      'Co-Owner First Name': [
        'co-owner first',
        'coowner first',
        'co owner first',
        'spouse first',
      ],
      'Co-Owner Last Name': [
        'co-owner last',
        'coowner last',
        'co owner last',
        'spouse last',
      ],
      'Email Address 2': [
        'email 2',
        'email address 2',
        'secondary email',
        'alternate email',
      ],
      Telephone: ['phone', 'telephone', 'tel', 'mobile', 'cell', 'contact'],
      'Street Address': ['address', 'street', 'street address', 'address 1'],
      'Address Line 2': [
        'unit',
        'apt',
        'apartment',
        'suite',
        'unit number',
        'address 2',
        'line 2',
      ],
      City: ['city', 'town'],
      State: ['state', 'st', 'province'],
      'Zip Code': ['zip', 'postal', 'zip code', 'postal code', 'zipcode'],
    };

    // Find best matches
    suggestions.forEach((mapping) => {
      const patterns =
        matchPatterns[mapping.internalField as keyof typeof matchPatterns] ||
        [];

      for (const header of available) {
        const headerLower = header.toLowerCase().trim();

        // Check for exact or pattern matches
        if (
          patterns.some(
            (pattern) =>
              headerLower === pattern || headerLower.includes(pattern),
          )
        ) {
          mapping.mappedColumn = header;
          const index = available.indexOf(header);
          if (index > -1) available.splice(index, 1);
          break;
        }
      }
    });

    setMappings(suggestions);
    setUnmappedColumns(available);
  };

  useEffect(() => {
    suggestMappings(data.headers);
  }, [data.headers]);

  const validateMappings = () => {
    const errors: string[] = [];
    const requiredFields = mappings.filter((m) => m.required);

    requiredFields.forEach((field) => {
      if (!field.mappedColumn) {
        errors.push(`${field.internalField} is required but not mapped`);
      }
    });

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    const { source, destination, draggableId } = result;

    setValidationErrors([]);

    if (
      source.droppableId === 'unmapped-columns' &&
      destination.droppableId.startsWith('field-')
    ) {
      // Mapping a column to a field
      const fieldIndex = parseInt(destination.droppableId.split('-')[1]);
      const columnName = draggableId;

      setMappings((prev) => {
        const newMappings = [...prev];

        // Clear any existing mapping for this column
        newMappings.forEach((m) => {
          if (m.mappedColumn === columnName) {
            m.mappedColumn = null;
          }
        });

        // Set new mapping
        newMappings[fieldIndex].mappedColumn = columnName;
        return newMappings;
      });

      setUnmappedColumns((prev) => prev.filter((col) => col !== columnName));
    } else if (
      source.droppableId.startsWith('field-') &&
      destination.droppableId === 'unmapped-columns'
    ) {
      // Unmapping a column from a field
      const fieldIndex = parseInt(source.droppableId.split('-')[1]);
      const columnName = draggableId;

      setMappings((prev) => {
        const newMappings = [...prev];
        newMappings[fieldIndex].mappedColumn = null;
        return newMappings;
      });

      setUnmappedColumns((prev) => [...prev, columnName]);
    } else if (
      source.droppableId.startsWith('field-') &&
      destination.droppableId.startsWith('field-')
    ) {
      // Moving between fields - swap or move mappings
      const sourceFieldIndex = parseInt(source.droppableId.split('-')[1]);
      const destFieldIndex = parseInt(destination.droppableId.split('-')[1]);
      const columnName = draggableId;

      if (sourceFieldIndex !== destFieldIndex) {
        setMappings((prev) => {
          const newMappings = [...prev];

          // Clear source mapping
          newMappings[sourceFieldIndex].mappedColumn = null;

          // If destination has a mapping, move it back to unmapped
          const existingMapping = newMappings[destFieldIndex].mappedColumn;
          if (existingMapping) {
            setUnmappedColumns((prevUnmapped) => [
              ...prevUnmapped,
              existingMapping,
            ]);
          }

          // Set new mapping
          newMappings[destFieldIndex].mappedColumn = columnName;

          return newMappings;
        });
      }
    }
  };

  const unmapField = (fieldIndex: number) => {
    const mapping = mappings[fieldIndex];
    if (mapping.mappedColumn) {
      const columnToUnmap = mapping.mappedColumn; // Capture the column name before clearing

      setMappings((prev) => {
        const newMappings = [...prev];
        newMappings[fieldIndex].mappedColumn = null;
        return newMappings;
      });

      setUnmappedColumns((prev) => [...prev, columnToUnmap]);
    }
  };

  const processData = () => {
    if (!validateMappings()) return;

    const processed = data.rows.map((row) => {
      const processedRow: any = {};

      mappings.forEach((mapping) => {
        if (mapping.mappedColumn) {
          const columnIndex = data.headers.indexOf(mapping.mappedColumn);
          processedRow[mapping.internalField] = row[columnIndex] || null;
        }
      });

      return processedRow;
    });

    onMappingComplete(mappings, processed);
  };

  const getSampleData = (columnName: string) => {
    const columnIndex = data.headers.indexOf(columnName);
    return data.rows
      .slice(0, 3)
      .map((row) => row[columnIndex])
      .filter(Boolean);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-primary" />
            Map Your Fields
          </CardTitle>
          <CardDescription>
            Drag your file columns to the appropriate database fields. Required
            fields must be mapped.
          </CardDescription>
        </CardHeader>
      </Card>

      {validationErrors.length > 0 && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <div>Please fix the following issues:</div>
            <ul className="list-disc list-inside mt-2">
              {validationErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Field Mapping Area */}
          <div className="lg:col-span-2 space-y-6">
            {/* Required Fields Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-destructive">
                Required Fields
              </h3>
              {mappings
                .filter((mapping) => mapping.required)
                .map((mapping, index) => {
                  const globalIndex = mappings.findIndex(
                    (m) => m.internalField === mapping.internalField,
                  );
                  return (
                    <Card
                      key={mapping.internalField}
                      className="p-4 border-destructive/20"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">
                            {mapping.internalField}
                          </span>
                          <Badge variant="destructive" className="text-xs">
                            Required
                          </Badge>
                          {mapping.mappedColumn && (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          )}
                        </div>
                      </div>

                      <Droppable droppableId={`field-${globalIndex}`}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className={`min-h-16 p-3 border-2 border-dashed rounded-lg transition-colors ${
                              snapshot.isDraggingOver
                                ? 'border-primary bg-drag-hover'
                                : mapping.mappedColumn
                                ? 'border-success bg-success/5'
                                : 'border-border'
                            }`}
                          >
                            {mapping.mappedColumn ? (
                              <Draggable
                                draggableId={mapping.mappedColumn}
                                index={0}
                              >
                                {(provided) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    className="bg-card border rounded-lg p-3 relative group"
                                  >
                                    <div
                                      className="flex items-center gap-2"
                                      {...provided.dragHandleProps}
                                    >
                                      <GripVertical className="w-4 h-4 text-muted-foreground cursor-move" />
                                      <div className="flex-1">
                                        <div className="font-medium text-tax-deepBlue">
                                          {mapping.mappedColumn}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                          Sample:{' '}
                                          {getSampleData(
                                            mapping.mappedColumn,
                                          ).join(', ')}
                                        </div>
                                      </div>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          unmapField(globalIndex);
                                        }}
                                      >
                                        <X className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </Draggable>
                            ) : (
                              <div className="text-center text-muted-foreground py-2">
                                Drop a column here
                              </div>
                            )}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </Card>
                  );
                })}
            </div>

            {/* Optional Fields Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-muted-foreground">
                Optional Fields
              </h3>
              {mappings
                .filter((mapping) => !mapping.required)
                .map((mapping, index) => {
                  const globalIndex = mappings.findIndex(
                    (m) => m.internalField === mapping.internalField,
                  );
                  return (
                    <Card key={mapping.internalField} className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">
                            {mapping.internalField}
                          </span>
                          {mapping.mappedColumn && (
                            <CheckCircle className="w-4 h-4 text-success" />
                          )}
                        </div>
                      </div>

                      <Droppable droppableId={`field-${globalIndex}`}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className={`min-h-16 p-3 border-2 border-dashed rounded-lg transition-colors ${
                              snapshot.isDraggingOver
                                ? 'border-primary bg-drag-hover'
                                : mapping.mappedColumn
                                ? 'border-success bg-success/5'
                                : 'border-border'
                            }`}
                          >
                            {mapping.mappedColumn ? (
                              <Draggable
                                draggableId={mapping.mappedColumn}
                                index={0}
                              >
                                {(provided) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    className="bg-card border rounded-lg p-3 relative group"
                                  >
                                    <div
                                      className="flex items-center gap-2"
                                      {...provided.dragHandleProps}
                                    >
                                      <GripVertical className="w-4 h-4 text-muted-foreground cursor-move" />
                                      <div className="flex-1">
                                        <div className="font-medium">
                                          {mapping.mappedColumn}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                          Sample:{' '}
                                          {getSampleData(
                                            mapping.mappedColumn,
                                          ).join(', ')}
                                        </div>
                                      </div>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          unmapField(globalIndex);
                                        }}
                                      >
                                        <X className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </Draggable>
                            ) : (
                              <div className="text-center text-muted-foreground py-2">
                                Drop a column here
                              </div>
                            )}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </Card>
                  );
                })}
            </div>
          </div>

          {/* Available Columns */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Available Columns</h3>
            <Card className="p-4">
              <Droppable droppableId="unmapped-columns">
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`min-h-32 space-y-2 ${
                      snapshot.isDraggingOver ? 'bg-drag-target' : ''
                    }`}
                  >
                    {unmappedColumns.map((column, index) => (
                      <Draggable
                        key={column}
                        draggableId={column}
                        index={index}
                      >
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className="bg-card border rounded-lg p-3 cursor-move hover:shadow-md transition-shadow"
                          >
                            <div className="flex items-center gap-2">
                              <GripVertical className="w-4 h-4 text-muted-foreground" />
                              <div>
                                <div className="font-medium">{column}</div>
                                <div className="text-xs text-muted-foreground">
                                  Sample: {getSampleData(column).join(', ')}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {unmappedColumns.length === 0 && (
                      <div className="text-center text-muted-foreground py-4">
                        All columns are mapped
                      </div>
                    )}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </Card>
          </div>
        </div>
      </DragDropContext>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Preview
        </Button>
        <Button onClick={processData} disabled={validationErrors.length > 0}>
          Process Import
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
